import { useCallback, useEffect, useState } from "react";

/**
 * Sticky sub-nav for long-scroll content pages (currently used by /biological-age).
 *
 * Behaviour:
 *  - Hides at the top until the user scrolls past `triggerOffset` pixels.
 *  - Auto-highlights the section currently in view (via IntersectionObserver).
 *  - Smooth-scrolls on click; the section's own `scroll-mt-28` handles header offset.
 *  - Horizontal scroll fallback on narrow screens so all anchors stay reachable.
 *
 * Anchors should be passed as: [{ id: "pillars", label: "The Pillars" }, ...]
 * Each `id` must exist as a real DOM id somewhere below.
 */
export default function ScrollSpyNav({ anchors, triggerOffset = 400 }) {
  const [active, setActive] = useState(anchors[0]?.id || "");
  const [pinned, setPinned] = useState(false);

  const handleWindowScroll = useCallback(() => {
    setPinned(window.scrollY > triggerOffset);
  }, [triggerOffset]);

  const handleIntersections = useCallback((entries) => {
    const visible = entries
      .filter((entry) => entry.isIntersecting)
      .sort((first, second) => second.intersectionRatio - first.intersectionRatio);
    if (visible[0]) setActive(visible[0].target.id);
  }, []);

  useEffect(() => {
    handleWindowScroll();
    window.addEventListener("scroll", handleWindowScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleWindowScroll);
  }, [handleWindowScroll]);

  // IntersectionObserver to highlight the current section
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;

    const obs = new IntersectionObserver(handleIntersections, {
      rootMargin: "-30% 0px -55% 0px",
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });
    anchors.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, [anchors, handleIntersections]);

  const handleClick = useCallback((e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Reflect in URL without triggering another scroll
      window.history.replaceState(null, "", `#${id}`);
    }
  }, []);

  return (
    <div
      data-testid="scrollspy-nav"
      className={`sticky top-[68px] z-30 transition-opacity duration-300 ${
        pinned ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      aria-hidden={!pinned}
    >
      <div className="border-y border-border bg-background/95 backdrop-blur-md">
        <nav
          aria-label="Section navigation"
          className="max-w-6xl mx-auto px-4 md:px-8 overflow-x-auto scrollbar-none"
        >
          <ul className="flex items-center gap-1 md:gap-2 py-2.5 whitespace-nowrap">
            {anchors.map(({ id, label }) => {
              const isActive = active === id;
              return (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    onClick={(e) => handleClick(e, id)}
                    data-testid={`scrollspy-link-${id}`}
                    aria-current={isActive ? "true" : undefined}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[0.65rem] uppercase tracking-[0.18em] transition-colors ${
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-foreground/65 hover:text-primary hover:bg-primary/8"
                    }`}
                  >
                    {label}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </div>
  );
}
